import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { HttpService } from '@api/http.service'
import { CustomerInfoActions } from './user-profile.actions'
import { catchError, map, switchMap, concatMap } from 'rxjs/operators'
import { of, withLatestFrom } from 'rxjs'
import { Router } from '@angular/router'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ErrorSnackBarComponent } from '../../utils/components/error-snack-bar/error-snack-bar.component'
import { SnackBarData } from '../../utils/components/error-snack-bar/data'
import { GroupsService } from '../../groups/groups.service'
import { environment } from '@environment/environment'
import { Store } from '@ngrx/store'
import { selectProfileState } from './user-profile.state'

@Injectable()
export class UserProfileEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly httpService: HttpService,
    private readonly router: Router,
    private readonly snackbar: MatSnackBar,
    private readonly groupsService: GroupsService,
    private readonly store: Store,
  ) {}

  $getUserInfo = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.customerInfoLoad),
      withLatestFrom(this.store.select(selectProfileState.profileState)),
      concatMap(([action, userInfo]) => {
        return this.httpService.getUserInfo(action.payload, userInfo.isGroupAdmin)
      }),
    )
  })

  $c4ApiLogin = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginLoad),
      switchMap(action => {
        return this.httpService.loginC4API(action.payload.uuidAuthent).pipe(
          switchMap(result => {
            const { create, update, read, delete: canDelete } = environment.features.groups
            const hasGroupFeature = create || update || read || canDelete
            if (hasGroupFeature) {
              return this.groupsService.getGroupsWhereUserIsAdmin().pipe(
                switchMap(groups => {
                  const isGroupAdmin = hasGroupFeature && groups.length > 0
                  // Success response
                  if (result.parameter.RESPONSE_OK === '1' || isGroupAdmin) {
                    return of(
                      CustomerInfoActions.c4LoginSuccess({
                        payload: {
                          sessionId: result?.parameter?.SESSION_ID ?? '',
                          uuid: result?.parameter?.PORTAL_UUID ?? '',
                          adrGr: result?.parameter?.DEF_ADR_GR ?? '',
                          isUserGroupAdmin: isGroupAdmin,
                        },
                      }),
                    )
                    // Failure response internally from C4API but C4API responded accordingly
                  } else {
                    if (
                      result.parameter.RESPONSE_ERROR.includes(
                        'Authentifizierung am Portal nicht möglich! Keine Adresse zur UUID',
                      )
                    ) {
                      this.router.navigateByUrl('/navigation/new-user')
                    }
                    return of(CustomerInfoActions.c4LoginFail())
                  }
                }),
              )
            } else {
              if (result.parameter.RESPONSE_OK === '1') {
                return of(
                  CustomerInfoActions.c4LoginSuccess({
                    payload: {
                      sessionId: result?.parameter?.SESSION_ID ?? '',
                      uuid: result?.parameter?.PORTAL_UUID ?? '',
                      adrGr: result?.parameter?.DEF_ADR_GR ?? '',
                    },
                  }),
                )
                // Failure response internally from C4API but C4API responded accordingly
              } else {
                if (
                  result.parameter.RESPONSE_ERROR.includes(
                    'Authentifizierung am Portal nicht möglich! Keine Adresse zur UUID',
                  )
                ) {
                  this.router.navigateByUrl('/navigation/new-user')
                }
                return of(CustomerInfoActions.c4LoginFail())
              }
            }
          }),
          // Server error like 404 or 500
          catchError(() => {
            this.snackbar.openFromComponent(ErrorSnackBarComponent, {
              duration: 10000,
              panelClass: ['error-snack-bar'],
            })
            return of(CustomerInfoActions.c4LoginFail())
          }),
        )
      }),
    )
  })

  $c4LoginSuccessCustomerInfo = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginSuccess),
      switchMap(action => {
        return this.httpService.getUserInfo(action.payload.uuid, action.payload.isUserGroupAdmin)
      }),
    )
  })

  $c4LoginFailureCustomerInfo = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginFail),
      switchMap(action => {
        return of(CustomerInfoActions.customerInfoFail())
      }),
    )
  })

  $getPaymentMethods = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.paymentMethodsLoad),
      switchMap(action => {
        return this.httpService.getUserPaymentMethods(action.payload).pipe(
          map(result => {
            return CustomerInfoActions.paymentMethodsSuccess({ payload: result.parameter })
          }),
          catchError(() => of(CustomerInfoActions.paymentMethodsFail())),
        )
      }),
    )
  })

  $c4LoginFailurePaymentMethods = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerInfoActions.c4LoginFail),
      switchMap(action => {
        return of(CustomerInfoActions.paymentMethodsFail())
      }),
    )
  })

  // $getPaymentMethodDetail = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(PaymentMethodActions.paymentMethodToEditLoad),
  //     switchMap(action => {
  //       return this.httpService.getUserPaymentMethodDetail(action.payload).pipe(
  //         map(result => {
  //           this.router.navigateByUrl('/navigation/account/payment-methods/edit')
  //           return PaymentMethodActions.paymentMethodToEditSuccess({ payload: result.parameter })
  //         }),
  //         catchError(() => of(PaymentMethodActions.paymentMethodToEditFail)),
  //       )
  //     }),
  //   )
  // })
  //
  // $setPaymentMethodToEdit = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(PaymentMethodActions.paymentMethodToEditSuccess),
  //     map(action => {
  //       return PaymentMethodActions.setPaymentMethodToEdit({
  //         payload: {
  //           bic: action.payload.BIC,
  //           iban: action.payload.IBAN,
  //           owner: action.payload.INHABER,
  //           type: 'sepa',
  //           bank: action.payload.BANKNAME,
  //           mandateReference: action.payload.MANDATS_REFERENZ,
  //         },
  //       })
  //     }),
  //   )
  // })
}
